/**
 * Lógica pura del acta de recepción: interpretar una lectura del escáner como
 * una marca, filtrar series, armar las filas del export. Sin React ni fetch,
 * para poder testearla con Vitest.
 */

import { pareceLecturaGs1, parsearStringGs1 } from '@/domain/gs1';
import type { MarcaInput } from '@/api/endpoints/recepcion-acta';
import type {
  EstadoActa,
  EstadoSerieActa,
  ExportRecepciones,
  SerieActa,
} from '@/api/contracts/recepcion-acta';
import { ESTADO_SERIE_ACTA_LABEL, MOTIVO_NO_RECIBIDA_LABEL } from '@/domain/types';
import type { MotivoNoRecibida } from '@/domain/types';

export type MarcaEscaneo = Extract<MarcaInput, { via: 'escaneo' }>;

export type LecturaInterpretada =
  | { ok: true; marca: MarcaEscaneo }
  | { ok: false; motivo: 'vacia' | 'sin_serie'; mensaje: string };

/** AI 17 `YYMMDD` → `YYYY-MM-DD`. Día 00 (GS1: "fin de mes") → último día del mes. */
export function vencimientoGs1AIso(yymmdd: string | null | undefined): string | null {
  const m = /^(\d{2})(\d{2})(\d{2})$/.exec(yymmdd ?? '');
  const yy = m?.[1];
  const mm = m?.[2];
  const dd = m?.[3];
  if (!yy || !mm || !dd) return null;
  const year = 2000 + Number(yy);
  const month = Number(mm);
  if (month < 1 || month > 12) return null;
  let day = Number(dd);
  const ultimo = new Date(Date.UTC(year, month, 0)).getUTCDate();
  if (day === 0 || day > ultimo) day = ultimo;
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/**
 * Convierte el texto del escáner (o lo tipeado) en la marca que se manda al
 * back. Un DataMatrix trae GTIN, lote, vencimiento y serie; un código lineal
 * (`21<serie>`, `(21)<serie>`) o una serie tipeada traen solo la serie, y el
 * back la ubica si es única en el acta.
 */
export function interpretarLectura(raw: string): LecturaInterpretada {
  const texto = (raw ?? '').replace(/[\r\n]+$/g, '').trim();
  if (!texto) return { ok: false, motivo: 'vacia', mensaje: 'Lectura vacía.' };

  if (pareceLecturaGs1(texto)) {
    const p = parsearStringGs1(texto);
    const serie = p.serie?.trim() ?? '';
    if (!serie) {
      return {
        ok: false,
        motivo: 'sin_serie',
        mensaje: p.gtin
          ? 'La lectura trae el código del producto pero no el número de serie: escaneá el DataMatrix de la unidad.'
          : 'La lectura no trae número de serie.',
      };
    }
    return {
      ok: true,
      marca: {
        via: 'escaneo',
        gtin: p.gtin,
        lote: p.lote?.trim() || null,
        serie,
        vencimiento: vencimientoGs1AIso(p.vencimiento),
        lectura: texto,
      },
    };
  }

  // Serie suelta. Si vino con el AI 21 entre paréntesis, lo sacamos; sin
  // paréntesis no se toca (una serie puede empezar con "21").
  const conAi = /^\(21\)(.+)$/.exec(texto);
  const serie = (conAi?.[1] ?? texto).trim();
  return { ok: true, marca: { via: 'escaneo', serie, lectura: texto } };
}

export const BADGE_ESTADO_ACTA: Record<EstadoActa, string> = {
  pendiente: 'badge-yellow',
  confirmando: 'badge-orange',
  trazado: 'badge-green',
  parcial: 'badge-blue',
  error: 'badge-red',
  anulada: 'badge-gray',
};

export const BADGE_ESTADO_SERIE: Record<EstadoSerieActa, string> = {
  pendiente: 'badge-gray',
  marcada: 'badge-blue',
  trazado: 'badge-green',
  error: 'badge-red',
  no_recibida: 'badge-yellow',
  anulada: 'badge-gray',
};

export interface FiltroSeries {
  texto?: string;
  gtin?: string;
  lote?: string;
  estado?: EstadoSerieActa | 'todas';
}

function norm(v: string | null | undefined): string {
  return (v ?? '').toLowerCase().trim();
}

/** Filtro de la tabla del acta: texto libre (serie, lote, GTIN, producto) + GTIN + lote + estado. */
export function filtrarSeries(series: readonly SerieActa[], f: FiltroSeries): SerieActa[] {
  const texto = norm(f.texto);
  const gtin = norm(f.gtin);
  const lote = norm(f.lote);
  return series.filter((s) => {
    if (gtin && norm(s.gtin) !== gtin) return false;
    if (lote && norm(s.lote) !== lote) return false;
    if (f.estado && f.estado !== 'todas' && s.estado !== f.estado) return false;
    if (texto) {
      const hay = [s.numeroSerial, s.lote, s.gtin, s.nombre, s.descripcionEvento]
        .map(norm)
        .some((v) => v.includes(texto));
      if (!hay) return false;
    }
    return true;
  });
}

/** Opciones únicas (GTIN → nombre) y lotes del acta, para los selectores. */
export function opcionesDeFiltro(series: readonly SerieActa[]) {
  const productos = new Map<string, string>();
  const lotes = new Set<string>();
  for (const s of series) {
    if (s.gtin && !productos.has(s.gtin)) productos.set(s.gtin, s.nombre ?? s.gtin);
    if (s.lote) lotes.add(s.lote);
  }
  return {
    productos: [...productos.entries()].map(([gtin, nombre]) => ({ gtin, nombre })),
    lotes: [...lotes].sort(),
  };
}

/** `YYYY-MM-DD` < hoy (comparación de strings ISO). */
export function estaVencida(vencimiento: string | null | undefined): boolean {
  if (!vencimiento) return false;
  const hoy = new Date().toISOString().slice(0, 10);
  return vencimiento.slice(0, 10) < hoy;
}

export function labelEstadoSerie(s: Pick<SerieActa, 'estado' | 'noRecibidaMotivo'>): string {
  if (s.estado === 'no_recibida' && s.noRecibidaMotivo) {
    return `${ESTADO_SERIE_ACTA_LABEL.no_recibida} · ${MOTIVO_NO_RECIBIDA_LABEL[s.noRecibidaMotivo]}`;
  }
  return ESTADO_SERIE_ACTA_LABEL[s.estado];
}

/* ------------------------------------------------------------------ */
/* Export para Plataforma                                              */
/* ------------------------------------------------------------------ */

export const EXPORT_SERIES_HEADERS = [
  'Acta',
  'Remito emisor',
  'Factura emisor',
  'GLN emisor',
  'Emisor',
  'GLN destino',
  'Estado acta',
  'Destino stock',
  'Cerrada el',
  'Cerrada por',
  'GTIN',
  'Producto',
  'Lote',
  'N° serie',
  'Vencimiento',
  'Evento ANMAT',
  'Fecha transacción',
  'Estado serie',
  'Confirmada el',
  'ID transacción',
  'ID confirmación ANMAT',
  'Marcada por',
  'Marcada vía',
  'Motivo no recibida',
  'Detalle no recibida',
  'Error ANMAT',
] as const;

const DESTINO_LABEL: Record<string, string> = { a: 'Disponible', na: 'No disponible' };

export function filasExportSeries(data: ExportRecepciones): unknown[][] {
  return data.series.map((r) => [
    r.actaId,
    r.numeroRemito,
    r.numeroFactura,
    r.glnOrigen,
    r.razonSocialOrigen,
    r.glnDestino,
    r.actaEstado,
    r.stockDestino ? (DESTINO_LABEL[r.stockDestino] ?? r.stockDestino) : null,
    r.cerradoAt,
    r.cerradoPor,
    r.gtin,
    r.nombre,
    r.lote,
    r.numeroSerial,
    r.vencimiento,
    r.descripcionEvento,
    r.fechaTransaccion,
    labelEstadoSerie({
      estado: r.estado as EstadoSerieActa,
      noRecibidaMotivo: (r.noRecibidaMotivo as MotivoNoRecibida | null) ?? null,
    }),
    r.tracedAt,
    r.idTransaccion,
    r.idTransacTraza,
    r.marcadaPor,
    r.marcadaVia,
    r.noRecibidaMotivo
      ? (MOTIVO_NO_RECIBIDA_LABEL[r.noRecibidaMotivo as MotivoNoRecibida] ?? r.noRecibidaMotivo)
      : null,
    r.noRecibidaDetalle,
    r.errorMessage,
  ]);
}

export const EXPORT_SOBRANTES_HEADERS = [
  'Acta',
  'Remito emisor',
  'Factura emisor',
  'GLN emisor',
  'Emisor',
  'GTIN',
  'Lote',
  'N° serie',
  'Vencimiento',
  'Lectura',
  'Observación',
  'Registrado el',
  'Registrado por',
] as const;

export function filasExportSobrantes(data: ExportRecepciones): unknown[][] {
  return data.sobrantes.map((r) => [
    r.actaId,
    r.numeroRemito,
    r.numeroFactura,
    r.glnOrigen,
    r.razonSocialOrigen,
    r.gtin,
    r.lote,
    r.numeroSerial,
    r.vencimiento,
    r.lectura,
    r.observacion,
    r.registradoAt,
    r.registradoPor,
  ]);
}
